import { useEffect, useState } from "react";
import usePageTitle from "@/hooks/usePageTitle";
import Table, { TableHeading, TableControls, TableFooter, type Row } from "@/components/Table";
import TabLayout from "../_TabLayout";
import { useQuery } from "@apollo/client";
import { VIDEOS_QUERY, VIDEOS_WITH_SCORM_QUERY } from "./queries";
import { Variables, VideoQuery, VideoNode } from "./types";
import { Path } from "@/router";
import SearchInput from "@/components/SearchInput";
import { ButtonWithLink } from "@/components/withLink";
import { cond, matches } from "lodash";
import Tag from "@/components/Tag";
import usePagination from "@/hooks/usePagination";
import MultiSelect from "@/components/MultiSelect";
import { snakeCase } from "lodash";
import { ColumnSort } from "@tanstack/react-table";
import useCapabilities, { PERMISSION } from "@/hooks/useCapabilities";
import { useParams, useSearchWithParams } from "@/hooks/useSearchParams";
import { toFormat } from "@/lib/dateHelpers";
import { useFlags } from "@/hooks/useFeatureFlags";

interface VideoRow extends VideoNode {}

const renderStatus = cond([
  [
    matches("Active"),
    () => (
      <Tag tagType="success" testId="active">
        Active
      </Tag>
    )
  ],
  [
    matches("Upcoming"),
    () => (
      <Tag tagType="information" testId="upcoming">
        Upcoming
      </Tag>
    )
  ],
  [
    matches("Expired"),
    () => (
      <Tag tagType="surface" testId="expired">
        Expired
      </Tag>
    )
  ]
]);

type ExtendedVariables = Variables & {
  stages?: string[];
  creditCategories?: string[];
  orderBy?: { field: string; direction: string };
};

export default function ContentPage() {
  usePageTitle("/education/videos", "Education | Videos");
  const { params, addSearchParam, deleteSearchParams, searchParams } = useParams();
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "searchTerm"
  });

  const [variables, setVariables] = useState<ExtendedVariables>(params);
  const [selectedStages, setSelectedStages] = useState<string[]>(params.stages || []);
  const [selectedCreditCategories, setSelectedCreditCategories] = useState<string[]>(params.creditCategories || []);
  const [orderBy, setOrderBy] = useState<ColumnSort[]>([{ id: "publishedAt", desc: true }]);

  const { hasPermission } = useCapabilities();
  const readOnly = !hasPermission(PERMISSION.manageVideos);
  const flags = useFlags();
  const scormFlagEnabled = !!flags["backoffice-scorm-courses"];

  useEffect(() => {
    resetPagination();
    setVariables({
      searchTerm: debouncedSearch || undefined,
      stages: selectedStages.length > 0 ? selectedStages : undefined,
      creditCategories: selectedCreditCategories.length > 0 ? selectedCreditCategories : undefined,
      orderBy: orderBy[0]
        ? { field: snakeCase(orderBy[0]?.id).toUpperCase(), direction: orderBy[0]?.desc ? "DESC" : "ASC" }
        : undefined
    });
  }, [debouncedSearch, selectedStages, selectedCreditCategories, orderBy, resetPagination]);

  // Flag-off selects identity fields only; flag-on adds status/launch fields.
  const { data, loading } = useQuery<VideoQuery>(scormFlagEnabled ? VIDEOS_WITH_SCORM_QUERY : VIDEOS_QUERY, {
    variables: { ...variables, ...paginationVariables }
  });

  const videoNodeData: VideoNode[] = data?.membershipVideos.nodes || [];
  const videoData = videoNodeData;

  const columnHeaders = {
    title: "Video Title",
    creditCategory: "Credit Type",
    series: "Series",
    publishedAt: "Publish Date",
    expiredAt: "Expiration Date",
    stage: "Stage"
  };

  const convertCreditCategory = (category: string) => {
    switch (category) {
      case "MEETINGS":
        return "Meetings";
      case "NATIONAL_SURVEYS":
        return "National Surveys";
      case "EDUCATION":
        return "Education";
      case "PLAYER_ENGAGEMENT":
        return "Player Engagement";
      case "OTHER":
        return "Other";
      default:
        return category;
    }
  };

  const getVideoStage = (video: VideoNode) => {
    const now = new Date();
    const publishedAt = new Date(video.publishedAt);
    const expiredAt = video.expiredAt ? new Date(video.expiredAt) : null;

    if (now >= publishedAt && (!expiredAt || now < expiredAt)) {
      return "Active";
    } else if (now < publishedAt) {
      return "Upcoming";
    } else if (expiredAt && now >= expiredAt) {
      return "Expired";
    }
    return "";
  };

  const handleSort = (updaterOrValue: any) => {
    const sortState = typeof updaterOrValue === "function" ? updaterOrValue([]) : updaterOrValue;
    const { id, desc } = sortState[0] || {};
    setOrderBy([{ id, desc }]);
  };

  const filterState = {
    stages: selectedStages.length > 0,
    creditCategories: selectedCreditCategories.length > 0
  };

  const activeFilterCount = Object.values(filterState).filter(Boolean).length;

  const handleClearAllFilters = () => {
    clearSearch();
    setSelectedStages([]);
    setSelectedCreditCategories([]);
    deleteSearchParams(["stages[]", "creditCategories[]", "searchTerm"]);
  };

  const columns = [
    {
      accessorKey: "title",
      header: columnHeaders["title"],
      meta: {
        sortType: "text" as const,
        headerClassName: "hide-header-in-column"
      }
    },
    ...(scormFlagEnabled
      ? [
          {
            accessorKey: "contentType",
            header: "Type",
            enableSorting: false,
            cell: ({ row }: { row: Row<VideoRow> }) => {
              const isScorm = row.original.contentType === "scorm";
              return isScorm ? (
                <Tag tagType="information" testId="content-type-scorm">
                  SCORM
                </Tag>
              ) : (
                <Tag tagType="surface" testId="content-type-vimeo">
                  Video
                </Tag>
              );
            }
          }
        ]
      : []),
    {
      accessorKey: "creditCategory",
      header: columnHeaders["creditCategory"],
      meta: {
        sortType: "text" as const
      },
      cell: ({ row }: { row: Row<VideoRow> }) => convertCreditCategory(row.original.creditCategory)
    },
    {
      accessorKey: "series",
      header: columnHeaders["series"],
      meta: {
        sortType: "text" as const
      }
    },
    {
      accessorKey: "publishedAt",
      header: columnHeaders["publishedAt"],
      meta: {
        sortType: "date" as const
      },
      cell: ({ row }: { row: Row<VideoRow> }) => {
        const date = row.original.publishedAt;
        return date ? toFormat(date) : null;
      }
    },
    {
      accessorKey: "expiredAt",
      header: columnHeaders["expiredAt"],
      meta: {
        sortType: "date" as const
      },
      cell: ({ row }: { row: Row<VideoRow> }) => {
        const date = row.original.expiredAt;
        return date ? toFormat(date) : null;
      }
    },
    {
      accessorKey: "stage",
      header: columnHeaders["stage"],
      enableSorting: false,
      meta: {
        headerClassName: "hide-header-in-column"
      },
      cell: ({ row }: { row: Row<VideoRow> }) => renderStatus(getVideoStage(row.original))
    }
  ];

  return (
    <TabLayout>
      <TableHeading
        body="Add videos and assessments for the Professional Development Requirement (PDR) Policy"
        actions={
          !readOnly
            ? [
                {
                  label: scormFlagEnabled ? "Add Course" : "Add Video",
                  icon: "add",
                  variant: "filled",
                  onClick: () => {},
                  render: () => (
                    <ButtonWithLink link={{ to: "/education/videos/publish-video" }} variant="filled" icon="add">
                      {scormFlagEnabled ? "Add Course" : "Add Video"}
                    </ButtonWithLink>
                  )
                }
              ]
            : undefined
        }
      />
      <Table
        data={videoData || []}
        loading={loading}
        getRowLink={(row) => ({
          to: `/education/videos/${row.original.id}?return_to=/education/videos&${searchParams}` as Path,
          state: { video: row.original }
        })}
        onSortingChange={handleSort}
        sortingState={orderBy}
        serverSideSorting={true}
        renderControls={(_table, { onSortClick }) => (
          <TableControls
            pagination={createHeaderPagination(data?.membershipVideos, {
              pageCount: videoData.length,
              loading
            })}
            onSortClick={onSortClick}
            useExpandedFilters={true}
            expandedFiltersState={filterState}
            onClearAllFilters={handleClearAllFilters}
            activeFilters={activeFilterCount}
          >
            <SearchInput
              placeholder="Search Videos"
              query={searchValue}
              onClear={clearSearch}
              onChange={(e) => {
                setSearchValue(e);
                resetPagination();
              }}
            />
            <div data-name="stages">
              <MultiSelect
                label="Stage"
                options={[
                  { value: "Active", label: "Active" },
                  { value: "Expired", label: "Expired" },
                  { value: "Upcoming", label: "Upcoming" }
                ]}
                selectedValues={selectedStages}
                onSelect={(value, checked) => {
                  const status = checked ? [...selectedStages, value] : selectedStages.filter((v) => v !== value);
                  setSelectedStages(status);
                  addSearchParam("stages[]", status.join(","));
                }}
                onClear={() => {
                  setSelectedStages([]);
                  deleteSearchParams("stages[]");
                }}
                required
              />
            </div>
            <div data-name="creditCategories">
              <MultiSelect
                label="Credit Type"
                options={[
                  { value: "EDUCATION", label: "Education" },
                  { value: "MEETINGS", label: "Meetings" },
                  { value: "NATIONAL_SURVEYS", label: "National Surveys" },
                  { value: "PLAYER_ENGAGEMENT", label: "Player Engagement" },
                  { value: "OTHER", label: "Other" }
                ]}
                selectedValues={selectedCreditCategories}
                onSelect={(value, checked) => {
                  const status = checked
                    ? [...selectedCreditCategories, value]
                    : selectedCreditCategories.filter((v) => v !== value);
                  setSelectedCreditCategories(status);
                  addSearchParam("creditCategories[]", status.join(","));
                }}
                onClear={() => {
                  setSelectedCreditCategories([]);
                  deleteSearchParams("creditCategories[]");
                }}
                required
              />
            </div>
          </TableControls>
        )}
        columns={columns}
        renderFooter={() => (
          <TableFooter
            pagination={createFooterPagination(data?.membershipVideos, {
              totalCount: data?.membershipVideos?.totalCount || 0,
              pageCount: videoData.length
            })}
          />
        )}
      />
    </TabLayout>
  );
}
